import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import PageHero from "@/components/PageHero";

const FILTERS = ["All", "Residential Construction", "Commercial Construction", "Civil Works", "Renovations & Maintenance"];

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  return (
    <div data-testid="projects-page">
      <PageHero
        eyebrow="Projects & Portfolio"
        title="Work that stands the test of time."
        subtitle="A growing record of completed and ongoing projects across Fiji. New photos are added as projects complete."
      />

      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-12">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`project-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold border transition-colors ${
                  filter === f
                    ? "bg-[#0A1A36] text-white border-[#0A1A36]"
                    : "bg-white text-[#0A1A36] border-[#E2E8F0] hover:border-[#0A1A36]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-slate-500 py-12" data-testid="projects-loading">Loading projects…</div>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-[#E2E8F0] py-16 text-center text-slate-500" data-testid="projects-empty">
              No projects in this category yet. New work is added regularly.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <article key={p.id} className="group bg-white border border-[#E2E8F0]" data-testid={`project-card-${p.id}`}>
                  <div className="overflow-hidden">
                    <img
                      src={p.image_url}
                      alt={p.title}
                      className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold">{p.category}</span>
                      <StatusBadge status={p.status} />
                    </div>
                    <h3 className="font-display text-lg font-bold mt-2 text-[#0A1A36]">{p.title}</h3>
                    {p.description && <p className="mt-2 text-sm text-slate-600 line-clamp-3">{p.description}</p>}
                    <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-500">
                      {p.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.location}</span>}
                      {p.year && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {p.year}</span>}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#0A1A36] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-6">
          <h3 className="font-display text-2xl sm:text-3xl font-bold max-w-2xl">
            Have a project in mind? Let&apos;s add it to this list next.
          </h3>
          <Link to="/quote" data-testid="projects-cta-quote">
            <Button className="rounded-none h-12 px-7 bg-[#D4AF37] hover:bg-[#b8952b] text-[#0A1A36] font-bold uppercase tracking-wider ag-btn-hover">
              Request a Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const map = {
    completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    ongoing: { label: "Ongoing", cls: "bg-[#D4AF37]/15 text-[#7a6212] border-[#D4AF37]/40" },
    upcoming: { label: "Upcoming", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  };
  const s = map[status] || map.completed;
  return (
    <span className={`text-[10px] uppercase tracking-[0.18em] font-bold border px-2 py-0.5 ${s.cls}`}>
      {s.label}
    </span>
  );
};
